import { forwardRef, type ElementType } from 'react';
import { styled, cx, type ComponentProps } from '@terra/ui-styled-system';
import { typographyClassNames } from './typography-class-names';

type TypographyOwnProps = {
  as?: ElementType;
};

const typographyBase = { margin: 0 } as const;

const StyledH1 = styled('h1', { base: typographyBase });
const StyledH2 = styled('h2', { base: typographyBase });
const StyledH3 = styled('h3', { base: typographyBase });
const StyledP = styled('p', { base: typographyBase });
const StyledLabel = styled('label', { base: typographyBase });
const StyledSpan = styled('span', { base: typographyBase });
const StyledCode = styled('code', { base: typographyBase });

type StyledH1Props = ComponentProps<typeof StyledH1> & TypographyOwnProps;
type StyledH2Props = ComponentProps<typeof StyledH2> & TypographyOwnProps;
type StyledH3Props = ComponentProps<typeof StyledH3> & TypographyOwnProps;
type StyledParagraphProps = ComponentProps<typeof StyledP> & TypographyOwnProps;
type StyledLabelProps = ComponentProps<typeof StyledLabel> & TypographyOwnProps;
type StyledSpanProps = ComponentProps<typeof StyledSpan> & TypographyOwnProps;
type StyledCodeProps = ComponentProps<typeof StyledCode> & TypographyOwnProps;

export const HeadingHero = forwardRef<HTMLHeadingElement, StyledH1Props>(
  function HeadingHero({ as, className, ...props }, ref) {
    return (
      <StyledH1
        ref={ref}
        as={as}
        className={cx(typographyClassNames.headingHero, className)}
        {...props}
      />
    );
  },
);
HeadingHero.displayName = 'HeadingHero';

export const HeadingPage = forwardRef<HTMLHeadingElement, StyledH1Props>(
  function HeadingPage({ as, className, ...props }, ref) {
    return (
      <StyledH1
        ref={ref}
        as={as}
        className={cx(typographyClassNames.headingPage, className)}
        {...props}
      />
    );
  },
);
HeadingPage.displayName = 'HeadingPage';

export const HeadingModal = forwardRef<HTMLHeadingElement, StyledH2Props>(
  function HeadingModal({ as, className, ...props }, ref) {
    return (
      <StyledH2
        ref={ref}
        as={as}
        className={cx(typographyClassNames.headingModal, className)}
        {...props}
      />
    );
  },
);
HeadingModal.displayName = 'HeadingModal';

export const HeadingSection = forwardRef<HTMLHeadingElement, StyledH2Props>(
  function HeadingSection({ as, className, ...props }, ref) {
    return (
      <StyledH2
        ref={ref}
        as={as}
        className={cx(typographyClassNames.headingSection, className)}
        {...props}
      />
    );
  },
);
HeadingSection.displayName = 'HeadingSection';

export const HeadingItem = forwardRef<HTMLHeadingElement, StyledH3Props>(
  function HeadingItem({ as, className, ...props }, ref) {
    return (
      <StyledH3
        ref={ref}
        as={as}
        className={cx(typographyClassNames.headingItem, className)}
        {...props}
      />
    );
  },
);
HeadingItem.displayName = 'HeadingItem';

export const BodyLarge = forwardRef<HTMLParagraphElement, StyledParagraphProps>(
  function BodyLarge({ as, className, ...props }, ref) {
    return (
      <StyledP
        ref={ref}
        as={as}
        className={cx(typographyClassNames.bodyLarge, className)}
        {...props}
      />
    );
  },
);
BodyLarge.displayName = 'BodyLarge';

export const BodyDefault = forwardRef<
  HTMLParagraphElement,
  StyledParagraphProps
>(function BodyDefault({ as, className, ...props }, ref) {
  return (
    <StyledP
      ref={ref}
      as={as}
      className={cx(typographyClassNames.bodyDefault, className)}
      {...props}
    />
  );
});
BodyDefault.displayName = 'BodyDefault';

export const BodyStrong = forwardRef<
  HTMLParagraphElement,
  StyledParagraphProps
>(function BodyStrong({ as, className, ...props }, ref) {
  return (
    <StyledP
      ref={ref}
      as={as}
      className={cx(typographyClassNames.bodyStrong, className)}
      {...props}
    />
  );
});
BodyStrong.displayName = 'BodyStrong';

export const LabelDefault = forwardRef<HTMLLabelElement, StyledLabelProps>(
  function LabelDefault({ as, className, ...props }, ref) {
    return (
      <StyledLabel
        ref={ref}
        as={as}
        className={cx(typographyClassNames.labelDefault, className)}
        {...props}
      />
    );
  },
);
LabelDefault.displayName = 'LabelDefault';

export const MetaDefault = forwardRef<HTMLSpanElement, StyledSpanProps>(
  function MetaDefault({ as, className, ...props }, ref) {
    return (
      <StyledSpan
        ref={ref}
        as={as}
        className={cx(typographyClassNames.metaDefault, className)}
        {...props}
      />
    );
  },
);
MetaDefault.displayName = 'MetaDefault';

export const MonoDefault = forwardRef<HTMLElement, StyledCodeProps>(
  function MonoDefault({ as, className, ...props }, ref) {
    return (
      <StyledCode
        ref={ref}
        as={as}
        className={cx(typographyClassNames.monoDefault, className)}
        {...props}
      />
    );
  },
);
MonoDefault.displayName = 'MonoDefault';

export const MonoSmall = forwardRef<HTMLElement, StyledCodeProps>(
  function MonoSmall({ as, className, ...props }, ref) {
    return (
      <StyledCode
        ref={ref}
        as={as}
        className={cx(typographyClassNames.monoSmall, className)}
        {...props}
      />
    );
  },
);
MonoSmall.displayName = 'MonoSmall';

export type HeadingHeroProps = ComponentProps<typeof HeadingHero>;
export type HeadingPageProps = ComponentProps<typeof HeadingPage>;
export type HeadingModalProps = ComponentProps<typeof HeadingModal>;
export type HeadingSectionProps = ComponentProps<typeof HeadingSection>;
export type HeadingItemProps = ComponentProps<typeof HeadingItem>;
export type BodyLargeProps = ComponentProps<typeof BodyLarge>;
export type BodyDefaultProps = ComponentProps<typeof BodyDefault>;
export type BodyStrongProps = ComponentProps<typeof BodyStrong>;
export type LabelDefaultProps = ComponentProps<typeof LabelDefault>;
export type MetaDefaultProps = ComponentProps<typeof MetaDefault>;
export type MonoDefaultProps = ComponentProps<typeof MonoDefault>;
export type MonoSmallProps = ComponentProps<typeof MonoSmall>;
